import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, User, X } from "lucide-react";
import {
  APIProvider,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart as RBarChart,
  Bar as RBar,
  PieChart as RPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  LabelList,
} from "recharts";
import {
  ColumnToggle,
  type DataTableColumn,
} from "@/admin/components/data-table";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { SearchInput } from "@/shared/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ProductFilterMenu,
  type ProductFilterFacet,
} from "@/shared/components/ui/product-filter-menu";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useTableColumnPreferences } from "@/admin/hooks/use-table-column-preferences";
import { useTableFilterPreferences } from "@/admin/hooks/use-table-filter-preferences";
import { useUserPreference } from "@/admin/hooks/use-user-preference";
import { useOrg } from "@/admin/hooks/use-org";
import {
  TimeFilter,
  type TimeRange,
} from "@/admin/components/shared/time-filter";
import { timeRangeToParams } from "@/admin/lib/time-range-params";
import {
  useNotificationFeed,
  type NotificationFeedParams,
  type NotificationRow,
} from "@/admin/hooks/use-notifications";
import { useWorkQueue } from "@/admin/hooks/use-work-queue";
import { formatCurrency, useFormattingContext } from "@/shared/lib/formatting";
import {
  formatClaimId,
  formatHoldingId,
  formatRecordId,
} from "@/admin/lib/formatting";
import { cn } from "@/shared/lib/utils";
import {
  useDashboardMetrics,
  useDashboardDataset,
  type DashboardDatasetEnvelope,
} from "@/admin/hooks/use-dashboard-config";
import {
  CLARA_VALE_MAP_STYLES,
  MAPS_KEY,
  missingMapConfig,
} from "@/shared/components/maps/map-config";
import type {
  CellKind,
  ColumnDef,
  DataSetRef,
  Datum,
  TileDef,
} from "./dashboard-types";
import { HeaderControls } from "./tile-header";
import { getLiveSource } from "./live-sources";
import { HeatMapTile } from "./heat-map-tile";
import { LiveTable } from "./live-table";
import { useDashboardCatalog } from "@/admin/hooks/use-dashboard-config";

/**
 * ======================================================================
 * MASTER FRAMEWORK - the ONE generic tile renderer.
 *
 * Every tile carries the SAME data envelope: `data.items: Datum[]`. `TileBody`
 * dispatches purely on `render_type` (the DRAW); each renderer reads the fields
 * of the shared `Datum` it needs. The chart and map renderers derive their
 * presentation-local shapes from the `Datum` list once, so the drawing code
 * stays identical no matter the tile. No per-render-type payload, no per-tenant
 * code, no hardcoded data.
 * ======================================================================
 */

export interface DrillField {
  l: string;
  v: string;
}

/**
 * The records behind a breakdown row: a live list `source` narrowed to one
 * `segment` ("<measure>:<label>", e.g. "by_product:Granule"). The list API
 * applies the same rule the card counted with, so they match its number.
 */
export interface DrillRecords {
  source: string;
  segment: string;
  /** The records table's own columns, sort and filters (a measure's raw table). */
  binding?: Partial<DataSetRef>;
}

/** What a card's rows open: its records table and the card's own filters. */
interface DrillContext {
  records: DataSetRef | null;
  filters: Record<string, string[]>;
}

/** A card's chosen filters as list params ("payment" -> "paid,unpaid"). */
function filterParams(values: Record<string, string[]>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([, v]) => v?.length)
      .map(([k, v]) => [k, v.join(",")]),
  );
}

export type DrillHandler = (
  ey: string,
  title: string,
  fields: DrillField[],
  records?: DrillRecords,
) => void;

/**
 * The records a breakdown card's row stands for, when the card is live. `label`
 * is the row's stored key when it has one (a field option's value), else its
 * label (column breakdowns match on the label itself).
 */
function segmentRecords(tile: TileDef, label: unknown): DrillRecords | undefined {
  if (label == null || label === "") return undefined;
  const [entity, measure] = (tile.data_set?.key ?? "").split(":");
  if (!measure) return undefined;
  // A measure with its own records table opens its rows there - the table's
  // columns, plus the card's own filters - so the records add up to the row.
  const drill = tile.meta?.drill as DrillContext | undefined;
  if (drill?.records?.source) {
    return {
      source: drill.records.source,
      segment: [drill.records.segment, `${measure}:${String(label)}`]
        .filter(Boolean)
        .join("|"),
      binding: {
        columns: drill.records.columns,
        defaultSort: drill.records.defaultSort,
        params: filterParams(drill.filters),
      },
    };
  }
  if (!/^(?:by|attr)_[a-z0-9_]+$/.test(measure) || !getLiveSource(entity)) return undefined;
  return { source: entity, segment: `${measure}:${String(label)}` };
}

interface TileBodyProps {
  tile: TileDef;
  onDrill: DrillHandler;
  /** The dashboard's selected date window; KPI metrics recompute for it. */
  createdAfter?: string | null;
}

/** The tile's inline data as a flat Datum list (empty when bound to data_set). */
const itemsOf = (tile: TileDef): Datum[] => tile.data?.items ?? [];

/** Map a live dataset envelope to the same flat Datum list renderers consume. */
function envelopeToItems(env: DashboardDatasetEnvelope | undefined): Datum[] {
  if (!env) return [];
  if (Array.isArray(env.series) && env.series.length) {
    // A single-series trend -> one datum per period (label = period, value).
    return env.series[0].data.map((p) => ({ label: p.period, value: p.value }));
  }
  if (Array.isArray(env.segments)) {
    // Leaderboard bar width reads meta.pct - derive it from the top value so a
    // live breakdown ranks visually like the inline-seeded ones do.
    const max = env.segments.reduce((m, s) => Math.max(m, s.value), 0) || 1;
    return env.segments.map((s) => ({
      label: s.label,
      value: s.value,
      meta: { pct: Math.round((s.value / max) * 100), key: s.key },
    }));
  }
  if (Array.isArray(env.items)) return env.items as Datum[];
  if (typeof env.value === "number") {
    // snapshot_number -> a single KPI datum. `format: "money"` (from a money
    // measure) rides along on meta so the KPI renders it in the org currency.
    return [
      {
        value: env.value,
        ...(typeof env.count === "number"
          ? { secondary: `across ${env.count.toLocaleString()} open claims` }
          : {}),
        meta: env.format ? { format: env.format } : undefined,
      },
    ];
  }
  return [];
}

/**
 * The Datum list a renderer should draw: inline `data.items` when the tile
 * carries them, otherwise the live envelope fetched for `data_set.key`. One
 * place, so every non-table renderer supports both inline and live data.
 */
function useTileItems(
  tile: TileDef,
  createdAfter?: string | null,
  filters?: Record<string, string[]>,
): { items: Datum[]; isPending: boolean; env?: DashboardDatasetEnvelope } {
  const key = tile.data_set?.key;
  const { data: env, isPending } = useDashboardDataset(key, createdAfter, undefined, filters);
  if (!key) return { items: itemsOf(tile), isPending: false };
  return { items: envelopeToItems(env), isPending, env };
}

/** A card's own filters (product template, status, ...), kept per card. */
function useCardFilters(tile: TileDef) {
  return useTableFilterPreferences(`dashboard-card-${tile.key}`);
}

/** The card's filter menu - the same Filters control every list uses. */
function CardFilters({
  facets,
  value,
  onChange,
}: {
  facets?: ProductFilterFacet[];
  value: Record<string, string[]>;
  onChange: (v: Record<string, string[]>) => void;
}) {
  if (!facets?.length) return null;
  return (
    <ProductFilterMenu
      facets={facets}
      value={value}
      onChange={onChange}
      triggerClassName="h-7 rounded-full border-[0.5px] border-[var(--rule)] px-2.5 text-[11.5px]"
    />
  );
}
/** A row's cells live on `datum.meta` (keyed by column field). */
const cellsOf = (d: Datum): Record<string, unknown> =>
  (d.meta ?? {}) as Record<string, unknown>;

// Presentation-local shapes derived from Datum, so the chart / map drawing code
// below is untouched by the data model.
type Bar = { x: string; v: number; hi?: boolean; k?: string };
type GeoPoint = { name: string; lat: number; lng: number; fail?: boolean };
const toBar = (d: Datum): Bar => ({
  x: String(d.label ?? ""),
  v: Number(d.value ?? 0),
  hi: d.state === "high",
  k: typeof d.meta?.key === "string" ? d.meta.key : undefined,
});
const toGeo = (d: Datum): GeoPoint => {
  const m = cellsOf(d);
  return {
    name: String(d.label ?? ""),
    lat: Number(m.lat ?? 0),
    lng: Number(m.lng ?? 0),
    fail: d.state === "fail",
  };
};

function Chip({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: "ok" | "warn";
}) {
  const toneCls =
    tone === "ok"
      ? "border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]"
      : tone === "warn"
        ? "border-[color-mix(in_srgb,var(--status-error)_38%,var(--rule))] bg-[var(--status-error-bg)] text-[var(--status-error)]"
        : "border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]";
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full border-[0.5px] px-[10px] py-[3px] text-[11.5px] font-medium ${toneCls}`}
    >
      {children}
    </span>
  );
}

// Render one table cell: `col.field` reads that key off the row Datum's meta.
function cell(row: Datum, col: ColumnDef): ReactNode {
  const cells = cellsOf(row);
  const v = cells[col.field];
  const text = v == null ? "" : String(v);
  const over = !!cells.over;
  const kind: CellKind | undefined = col.kind;
  if (kind === "claim")
    return (
      <span className="font-mono text-[13px] font-medium text-[var(--green)]">
        {text}
      </span>
    );
  if (kind === "chip") {
    // Trend chips read at a glance: rising green, falling red, flat neutral.
    const tone = text.startsWith("▲")
      ? "ok"
      : text.startsWith("▼")
        ? "warn"
        : undefined;
    return <Chip tone={tone}>{text}</Chip>;
  }
  if (kind === "tis" || kind === "countdown")
    return (
      <span
        className={`tabular-nums ${over ? "font-semibold text-[var(--status-error)]" : "text-[var(--ink-2)]"}`}
      >
        {over && <span className="mr-1 align-middle text-[8px]">●</span>}
        {text}
      </span>
    );
  if (kind === "money")
    return <span className="tabular-nums text-[var(--ink)]">{text}</span>;
  if (kind === "strong")
    return <b className="font-medium text-[var(--ink)]">{text}</b>;
  if (kind === "appt") {
    const ok = v === true || v === "confirmed";
    return (
      <Chip tone={ok ? "ok" : "warn"}>{ok ? "Confirmed" : "Unconfirmed"}</Chip>
    );
  }
  return <span className="text-[var(--ink-2)]">{text}</span>;
}

// A borderless, compact "worklist" - div-based rows (not a DataTable): this is a
// scannable list, and each row drills into its record. Rows are Datum whose meta
// holds the cells; the columns to show come from tile.meta.columns.
function Worklist({
  tile,
  onDrill,
  showColumnControl = true,
}: TileBodyProps & { showColumnControl?: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Reuse the list tables' Columns control + per-user persistence so a user can
  // show/hide and reorder a tile's columns for their own workflow. Keyed per
  // tile per user; with no stored prefs it renders exactly the seeded columns.
  const {
    columnVisibility,
    setColumnVisibility,
    saveColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnOrder,
  } = useTableColumnPreferences(`dashboard-tile-${tile.key ?? "tile"}`);
  const allColumns: ColumnDef[] =
    tile.meta?.columns ??
    (tile.meta?.variant === "loa"
      ? [
          { label: "Claim", field: "n", kind: "claim" },
          { label: "Owner", field: "who" },
          { label: "In status", field: "tis", kind: "tis" },
          { label: "Amount", field: "amount", kind: "money" },
        ]
      : [
          { label: "Claim", field: "n", kind: "claim" },
          { label: "Type", field: "t", kind: "chip" },
          { label: "Status", field: "s" },
          { label: "In status", field: "tis", kind: "tis" },
          { label: "Owner", field: "owner" },
        ]);
  const rows = itemsOf(tile);

  // Apply the user's saved order then visibility (default: all shown, seeded
  // order). Never render zero columns - fall back to the full set.
  const byField = new Map(allColumns.map((c) => [c.field, c]));
  const orderedColumns = columnOrder.length
    ? [
        ...columnOrder
          .map((f) => byField.get(f))
          .filter((c): c is ColumnDef => !!c),
        ...allColumns.filter((c) => !columnOrder.includes(c.field)),
      ]
    : allColumns;
  const displayColumns = orderedColumns.filter(
    (c) => columnVisibility[c.field] !== false,
  );
  const columns = displayColumns.length > 0 ? displayColumns : allColumns;
  const toggleColumns: DataTableColumn<Datum>[] = allColumns.map((c) => ({
    id: c.field,
    header: c.label,
  }));

  if (tile.meta?.caughtUp && rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="text-[26px] text-[var(--green)]">✦</div>
        <h4 className="mt-2 font-display text-[22px] font-light text-[var(--ink)]">
          {t("dashboard.caughtUpTitle", "You’re all caught up")}
        </h4>
        <p className="mt-1 text-[13px] text-[var(--ink-3)]">
          {t("dashboard.caughtUpSub", "Nothing needs you right now.")}
        </p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-[14px] text-[var(--ink-2)]">
        {t("dashboard.noResults", "No results meet the parameter.")}
      </p>
    );
  }

  const gridCols = columns.map(() => "minmax(0,1fr)").join(" ");
  // Wide, spec-driven tables (11-12 columns) get a wider scroll floor so each
  // column stays legible; the tile scrolls horizontally within its own box. A
  // short table (a category and its total) always fits the card, half width too.
  const minWidth = columns.length <= 3 ? 0 : Math.max(520, columns.length * 112);
  return (
    <div>
      {showColumnControl && (
        <HeaderControls>
          <ColumnToggle
            columns={toggleColumns}
            visibility={columnVisibility}
            onVisibilityChange={(v) => {
              setColumnVisibility(v);
              saveColumnVisibility(v);
            }}
            columnOrder={
              columnOrder.length ? columnOrder : allColumns.map((c) => c.field)
            }
            onColumnOrderChange={(o) => {
              setColumnOrder(o);
              saveColumnOrder(o);
            }}
          />
        </HeaderControls>
      )}
      {/* Scrolls both ways within a fixed-height box (matching the live
          tables' max-h-[440px]), so a long list scrolls instead of stretching
          the tile; the column header stays pinned while rows scroll. */}
      <div className="max-h-[440px] overflow-auto">
        <div style={{ minWidth }}>
          <div
            className="sticky top-0 z-[1] grid items-center gap-3 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] pb-2.5"
            style={{ gridTemplateColumns: gridCols }}
          >
            {columns.map((c) => (
              <span
                key={c.label}
                className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-2)]"
              >
                {c.label}
              </span>
            ))}
          </div>
          {rows.map((r, i) => {
            const cells = cellsOf(r);
            return (
              <Button
                key={i}
                variant="ghost"
                onClick={() => {
                  // A row deep-links into its own record: `href` is a full path
                  // (e.g. a registration record); a bare `uuid` opens the claim.
                  // Rows with neither fall back to the read-only detail drawer.
                  const href = cells.href;
                  if (typeof href === "string" && href) {
                    navigate(href);
                    return;
                  }
                  const uuid = cells.uuid;
                  if (typeof uuid === "string" && uuid) {
                    navigate(`/claims/${uuid}`);
                    return;
                  }
                  onDrill(
                    tile.title ?? "Card",
                    String(cells[columns[0].field] ?? "Record"),
                    columns.map((c) => ({
                      l: c.label,
                      v: cells[c.field] == null ? "-" : String(cells[c.field]),
                    })),
                    // A live breakdown row opens the records it counted.
                    segmentRecords(tile, cells.k ?? cells.x),
                  );
                }}
                className="grid h-auto w-full items-center gap-3 whitespace-normal rounded-[6px] border-b-[0.5px] border-[var(--rule)] px-0 py-2 text-left text-[13px] font-normal text-[var(--ink)] transition-colors last:border-b-0 hover:bg-[var(--bg-subtle)]"
                style={{ gridTemplateColumns: gridCols }}
              >
                {columns.map((c) => (
                  <span key={c.label} className="min-w-0 truncate">
                    {cell(r, c)}
                  </span>
                ))}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// stat: each Datum is a KPI - label + value (fallback), meta.metric pulls the
// live number, secondary is the description, state==='warn' emphasises red.
// The list page each buildable entity's records live on, so a "<entity>:total"
// stat card can link straight to them. Warranties are viewed under a holding's
// registration (there is no standalone warranties list), matching the worklist
// row links in live-sources.tsx.
const ENTITY_LIST_PATH: Record<string, string> = {
  registrations: "/registrations",
  claims: "/claims",
  transfers: "/transfers",
  inspections: "/inspections",
  warranties: "/registrations",
};

// A measure that counts a DIFFERENT entity than its dataset prefix links to
// that entity's list instead. "registrations:distinct_dealers" counts partners
// derived from registrations, so its way in is the partner network - not the
// registrations list the figure was computed from.
const MEASURE_LIST_PATH: Record<string, string> = {
  distinct_dealers: "/network/partners",
  by_dealer: "/network/partners",
  // The signed-in user's own claims.
  my_cycle_time: "/claims?view=mine",
};

function Kpi({
  tile,
  createdAfter,
}: {
  tile: TileDef;
  createdAfter?: string | null;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale, currency } = useFormattingContext();
  // Where the pointer went down on a card, so a drag across it (to select and
  // copy the number) is not mistaken for a click that opens the list.
  const pressAt = useRef<{ x: number; y: number } | null>(null);
  const { data: metrics } = useDashboardMetrics(createdAfter);
  // Inline KPI grids carry their items (each with meta.metric); a built
  // stat card binds a data_set.key ("<entity>:total") whose snapshot value
  // arrives as a single datum. useTileItems serves both.
  const { items } = useTileItems(tile, createdAfter);
  // Money measures ride in with meta.format === "money": real payment totals,
  // shown to the cent in the org currency.
  const money = (v: number) => formatCurrency(v, currency, locale);
  // A total-count card links to its entity's list page (registrations / claims /
  // ...), so the number is a way in to the underlying records.
  const datasetEntity = tile.data_set?.key?.includes(":")
    ? tile.data_set.key.split(":")[0]
    : undefined;
  const datasetMeasure = tile.data_set?.key?.includes(":")
    ? tile.data_set.key.split(":")[1]
    : undefined;
  const datasetTo =
    (datasetMeasure ? MEASURE_LIST_PATH[datasetMeasure] : undefined) ??
    (datasetEntity ? ENTITY_LIST_PATH[datasetEntity] : undefined);
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(156px,1fr))] gap-3">
      {items.map((it, i) => {
        // A metric-bound KPI shows the live computed number; the seed value is the
        // fallback while metrics load or when the metric is unavailable.
        const metric = it.meta?.metric as string | undefined;
        const liveValue = metric ? metrics?.[metric] : undefined;
        const raw = liveValue == null ? it.value : liveValue;
        const fmt = it.meta?.format;
        const value =
          raw == null
            ? ""
            : fmt === "money" && typeof raw === "number"
              ? money(raw)
              : fmt === "percent" && typeof raw === "number"
                ? `${raw}%`
                : fmt === "days" && typeof raw === "number"
                  ? `${raw} ${raw === 1 ? "day" : "days"}`
                  : String(raw);
        const warn = it.state === "warn";
        // Inline items carry their own link; a dataset stat card links to its
        // entity's list page. The label falls back to a "View all" affordance so
        // the click target reads clearly (the card title names what it counts).
        const to = it.to ?? datasetTo;
        const clickable = !!to;
        const label =
          it.label ??
          (datasetTo ? t("dashboard.kpi.viewAll", "View all") : undefined);
        return (
          <Button
            key={it.label ?? `kpi-${i}`}
            variant="ghost"
            // Not `disabled`: a disabled button swallows pointer events in some
            // browsers, which would stop the number from being selected and
            // copied. Unclickable cards are announced as unavailable and kept
            // out of the tab order instead.
            aria-disabled={!clickable || undefined}
            tabIndex={clickable ? undefined : -1}
            onPointerDown={(e) => {
              pressAt.current = { x: e.clientX, y: e.clientY };
            }}
            onClick={
              clickable
                ? (e) => {
                    const from = pressAt.current;
                    pressAt.current = null;
                    // Enter / Space (detail 0) always opens, whatever a
                    // cancelled drag left behind.
                    if (e.detail === 0) {
                      navigate(to as string);
                      return;
                    }
                    // A drag across the number to copy it ends in a click; a
                    // drag or a selection inside the card means "copy", not
                    // "open".
                    const dragged =
                      !!from &&
                      Math.abs(e.clientX - from.x) +
                        Math.abs(e.clientY - from.y) >
                        5;
                    const sel = window.getSelection();
                    const selected =
                      !!sel &&
                      !sel.isCollapsed &&
                      !!sel.anchorNode &&
                      e.currentTarget.contains(sel.anchorNode);
                    if (dragged || selected) return;
                    navigate(to as string);
                  }
                : undefined
            }
            className={`group flex h-auto w-full flex-col items-start justify-start whitespace-normal rounded-[13px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4 text-left font-normal transition-[transform,border-color,background-color] duration-150 ease-[var(--ease-enter)] ${
              clickable
                ? "cursor-pointer hover:border-[var(--green-border)] hover:bg-[var(--green-bg)] active:scale-[0.985]"
                : "cursor-default hover:border-[var(--rule-strong)]"
            }`}
          >
            <div
              className={`font-display text-[38px] font-light leading-none tabular-nums select-text ${warn ? "text-[var(--status-error)]" : "text-[var(--ink)]"}`}
            >
              {value}
            </div>
            <div className="mt-2.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.11em] text-[var(--ink-2)]">
              {label}
              {clickable && (
                <ArrowUpRight
                  size={13}
                  className="opacity-0 transition-opacity group-hover:opacity-100 text-[var(--green)]"
                />
              )}
            </div>
            <div className="mt-1.5 text-[13px] text-[var(--ink-2)]">
              {it.secondary}
            </div>
          </Button>
        );
      })}
    </div>
  );
}

// Per-slice palette for the donut + its legend. Kept as {token, opacity} pairs
// so both the recharts <Cell> (fill/fillOpacity props) and the legend dots
// (Tailwind arbitrary utilities) draw the same colour. `hi` uses the error tone.
// Only the accent (--green) and ink tokens, at stepped strengths: a tenant theme
// can set --gold to white (one tenant does), which made slices vanish. Eight
// distinct steps - a chart draws at most CHART_MAX_CATEGORIES (8) slices.
const PIE_SLICES: { fill: string; opacity: number }[] = [
  { fill: "var(--green)", opacity: 1 },
  { fill: "var(--ink)", opacity: 1 },
  { fill: "var(--green)", opacity: 0.6 },
  { fill: "var(--ink)", opacity: 0.7 },
  { fill: "var(--green)", opacity: 0.35 },
  { fill: "var(--ink)", opacity: 0.45 },
  { fill: "var(--green)", opacity: 0.8 },
  { fill: "var(--ink)", opacity: 0.25 },
];
const SEGMENT_DOT = [
  "bg-[var(--green)]",
  "bg-[var(--ink)]",
  "bg-[color-mix(in_srgb,var(--green)_60%,transparent)]",
  "bg-[color-mix(in_srgb,var(--ink)_70%,transparent)]",
  "bg-[color-mix(in_srgb,var(--green)_35%,transparent)]",
  "bg-[color-mix(in_srgb,var(--ink)_45%,transparent)]",
  "bg-[color-mix(in_srgb,var(--green)_80%,transparent)]",
  "bg-[color-mix(in_srgb,var(--ink)_25%,transparent)]",
];

// Shared, theme-aware tooltip for the recharts draws: the category + its value,
// in the house card idiom (no default recharts chrome).
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value?: number | string }>;
  label?: string | number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-2.5 py-1.5 shadow-[var(--shadow-float)]">
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
        {label}
      </div>
      <div className="text-[13px] font-semibold tabular-nums text-[var(--ink)]">
        {payload[0]?.value}
      </div>
    </div>
  );
}

const AXIS_TICK = { fontSize: 10.5, fill: "var(--ink-2)" } as const;

// Clicking a category (bar / point / slice) opens its rows in the shared
// Worklist. Recharts hands the active x-label on the chart's onClick.
function openFromChart(
  state: { activeLabel?: string | number } | null,
  onOpen: (x: string) => void,
) {
  const l = state?.activeLabel;
  if (l != null) onOpen(String(l));
}

// A vertical bar chart (recharts). `hi` bars are solid; the rest are a light
// tint of the tenant accent. Value labels sit above each bar.
function BarChart({
  bars,
  onOpen,
}: {
  bars: Bar[];
  onOpen: (x: string) => void;
}) {
  return (
    <div className="h-[180px] w-full pt-1">
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart
          data={bars}
          margin={{ top: 18, right: 8, left: 8, bottom: 0 }}
          onClick={(s) => openFromChart(s, onOpen)}
        >
          <CartesianGrid
            vertical={false}
            stroke="var(--rule)"
            strokeOpacity={0.5}
          />
          <XAxis
            dataKey="x"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            tick={AXIS_TICK}
          />
          <YAxis hide domain={[0, "dataMax"]} />
          <RTooltip
            cursor={{ fill: "var(--bg-subtle)" }}
            content={<ChartTooltip />}
          />
          <RBar
            dataKey="v"
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="v"
              position="top"
              fill="var(--ink-2)"
              fontSize={11}
            />
            {bars.map((b, i) => (
              // Solid accent bars, matching the leaderboard / trend charts. A
              // `high`-state bar switches to the error tone rather than a washed
              // opacity, so emphasis reads without looking translucent.
              <Cell
                key={i}
                fill={b.hi ? "var(--status-error)" : "var(--green)"}
                cursor="pointer"
              />
            ))}
          </RBar>
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}

// A donut (recharts) with a click-through legend listing each slice's share.
function PieChart({
  bars,
  onOpen,
}: {
  bars: Bar[];
  onOpen: (x: string) => void;
}) {
  const total = Math.max(
    1,
    bars.reduce((s, b) => s + b.v, 0),
  );
  return (
    <div className="flex items-center gap-6 pt-1">
      <div className="h-[150px] w-[150px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <RPieChart>
            <Pie
              data={bars}
              dataKey="v"
              nameKey="x"
              innerRadius={42}
              outerRadius={64}
              paddingAngle={1}
              stroke="none"
              isAnimationActive={false}
              onClick={(_, i) => onOpen(bars[i].x)}
            >
              {bars.map((b, i) => {
                const c = b.hi
                  ? { fill: "var(--status-error)", opacity: 1 }
                  : PIE_SLICES[i % PIE_SLICES.length];
                return (
                  <Cell
                    key={i}
                    fill={c.fill}
                    fillOpacity={c.opacity}
                    cursor="pointer"
                  />
                );
              })}
            </Pie>
            <RTooltip content={<ChartTooltip />} />
          </RPieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {bars.map((b, i) => {
          const pct = Math.round((b.v / total) * 100);
          const dot = b.hi
            ? "bg-[var(--status-error)]"
            : SEGMENT_DOT[i % SEGMENT_DOT.length];
          return (
            <Button
              key={`${b.x}-${i}-legend`}
              variant="ghost"
              onClick={() => onOpen(b.x)}
              className="flex h-auto w-full items-center justify-start gap-2.5 rounded-[6px] px-1.5 py-1 text-left font-normal hover:bg-[var(--bg-subtle)]"
            >
              <span className={`h-2.5 w-2.5 flex-none rounded-full ${dot}`} />
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--ink)]">
                {b.x}
              </span>
              <span className="tabular-nums text-[12px] font-medium text-[var(--ink-2)]">
                {b.v}
              </span>
              <span className="w-9 text-right tabular-nums text-[11px] text-[var(--ink-3)]">
                {pct}%
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

// A smooth trend area (recharts) with a soft accent gradient fill and hoverable
// points; clicking a point opens that period's detail.
function LineChart({
  bars,
  onOpen,
}: {
  bars: Bar[];
  onOpen: (x: string) => void;
}) {
  return (
    <div className="h-[180px] w-full pt-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={bars}
          margin={{ top: 12, right: 10, left: 10, bottom: 0 }}
          onClick={(s) => openFromChart(s, onOpen)}
        >
          <defs>
            <linearGradient id="cv-trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--green)" stopOpacity={0.22} />
              <stop offset="100%" stopColor="var(--green)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid
            vertical={false}
            stroke="var(--rule)"
            strokeOpacity={0.5}
          />
          <XAxis
            dataKey="x"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            tick={AXIS_TICK}
          />
          <YAxis hide domain={[0, "dataMax"]} />
          <RTooltip
            cursor={{ stroke: "var(--rule-strong)" }}
            content={<ChartTooltip />}
          />
          <Area
            type="monotone"
            dataKey="v"
            stroke="var(--green)"
            strokeWidth={2}
            fill="url(#cv-trend-fill)"
            dot={{ r: 3, fill: "var(--green)", strokeWidth: 0 }}
            activeDot={{ r: 5, cursor: "pointer" }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * The records behind a chart, when the catalogue defines a records table for
 * its measure (``raw_tables["<source>:<measure>"]``, tenant-overridable). Then
 * the card's "Table" is that list of records - the columns the measure is
 * about - not a two-column count per category.
 */
function useRecordsBinding(tile: TileDef): {
  records: DataSetRef | null;
  /** The card's "Table" is that list (the measure offers no count table). */
  tableIsRecords: boolean;
} {
  const { data: catalog } = useDashboardCatalog();
  const key = tile.data_set?.key;
  const preset = key ? catalog?.raw_tables?.[key] : undefined;
  if (!key || !preset) return { records: null, tableIsRecords: false };
  const [entity, measure] = key.split(":");
  const records = { source: entity, measure, limit: 20, ...preset } as DataSetRef;
  if (!getLiveSource(records.source)) return { records: null, tableIsRecords: false };
  const renders = catalog?.measures.find((m) => m.key === measure)?.renders;
  return { records, tableIsRecords: !!renders && !renders.includes("table") };
}

/** Most categories a bar or pie chart draws before it stops being readable. */
const CHART_MAX_CATEGORIES = 8;

// A chart tile: the SAME Datum list drawn as a bar, pie, or line chart (per
// render_type), each with a Chart/Table toggle. Clicking a category opens its
// rows in the shared Worklist - one renderer, many draws, zero hardcoded data.
function ChartTile({
  tile,
  onDrill,
  kind,
  createdAfter,
}: TileBodyProps & { kind: "bar" | "pie" | "line" }) {
  const { t } = useTranslation();
  const [cardFilters, setCardFilters] = useCardFilters(tile);
  const { items, env, isPending } = useTileItems(tile, createdAfter, cardFilters);
  const allBars = items.map(toBar);
  // A breakdown arrives with every category (largest first). Bar and pie
  // charts draw only the top few so they stay legible; the Table toggle below
  // lists them all. A line chart is a time series and is never trimmed.
  const bars =
    kind === "line" ? allBars : allBars.slice(0, CHART_MAX_CATEGORIES);
  const [mode, setMode] = useState<"chart" | "table">("chart");
  const [filterX, setFilterX] = useState<string | null>(null);
  const { records, tableIsRecords } = useRecordsBinding(tile);
  const drillTile = withDrill(tile, records, cardFilters);

  // Loading first, so a chart still fetching never reads as empty.
  if (isPending) {
    return (
      <div className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }

  // Table view of the chart: one row per category (label -> value).
  const columns = countColumns(tile, env);
  const allRows: Datum[] = allBars.map((b) => ({ meta: { x: b.x, v: b.v, k: b.k } }));
  const rows = filterX
    ? allRows.filter((r) => String(cellsOf(r).x) === filterX)
    : allRows;

  const openBar = (x: string) => {
    const bar = allBars.find((b) => b.x === x);
    // A breakdown with its own records table opens the bar's records.
    const drill = records && env?.segments ? segmentRecords(drillTile, bar?.k ?? x) : undefined;
    if (drill) {
      onDrill(
        tile.title ?? "Card",
        x,
        [
          { l: columns[0].label, v: x },
          { l: columns[1].label, v: String(bar?.v ?? "") },
        ],
        drill,
      );
      return;
    }
    // A records table lists every record, not one category's: open it whole.
    setFilterX(tableIsRecords ? null : x);
    setMode("table");
  };

  const segBtn = (active: boolean) =>
    `h-auto rounded-full px-3 py-[3px] text-[11px] font-normal transition-all ${
      active
        ? "bg-[var(--bg-card)] text-[var(--ink)] shadow-[0_1px_2px_rgba(26,26,24,0.08)]"
        : "text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
    }`;

  return (
    <div>
      <HeaderControls>
        <div className="flex items-center gap-2">
          <CardFilters facets={env?.filters} value={cardFilters} onChange={setCardFilters} />
          {mode === "table" && filterX && (
            <Button
              variant="ghost"
              onClick={() => setFilterX(null)}
              className="inline-flex h-auto items-center gap-1 rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2.5 py-[3px] text-[11px] font-normal text-[var(--green)] hover:bg-[var(--green-bg)]"
            >
              {filterX}
              <X size={11} />
            </Button>
          )}
          <div className="inline-flex rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-0.5">
            <Button
              variant="ghost"
              aria-pressed={mode === "chart"}
              onClick={() => setMode("chart")}
              className={segBtn(mode === "chart")}
            >
              Chart
            </Button>
            <Button
              variant="ghost"
              aria-pressed={mode === "table"}
              onClick={() => {
                setFilterX(null);
                setMode("table");
              }}
              className={segBtn(mode === "table")}
            >
              Table
            </Button>
          </div>
        </div>
      </HeaderControls>

      {mode === "chart" ? (
        kind === "pie" ? (
          <PieChart bars={bars} onOpen={openBar} />
        ) : kind === "line" ? (
          <LineChart bars={bars} onOpen={openBar} />
        ) : (
          <BarChart bars={bars} onOpen={openBar} />
        )
      ) : tableIsRecords && records ? (
        <LiveTable
          key={`records:${tile.key}`}
          source={records.source!}
          binding={records}
          tableId={`dashboard-tile-${tile.key}-records`}
        />
      ) : (
        <Worklist
          tile={{
            ...drillTile,
            data: { items: rows },
            meta: { ...drillTile.meta, columns },
          }}
          onDrill={onDrill}
          showColumnControl={false}
        />
      )}
    </div>
  );
}

/** The two columns of a count table: the category and its total. */
function countColumns(tile: TileDef, env?: DashboardDatasetEnvelope): ColumnDef[] {
  return [
    {
      label: env?.labels?.category ?? (tile.meta?.unit ? tile.meta.unit : "Category"),
      field: "x",
      kind: "strong",
    },
    { label: env?.labels?.value ?? "Count", field: "v" },
  ];
}

/** The tile, carrying what its rows open (its records table and filters). */
function withDrill(
  tile: TileDef,
  records: DataSetRef | null,
  filters: Record<string, string[]>,
): TileDef {
  if (!records) return tile;
  const drill: DrillContext = { records, filters };
  return { ...tile, meta: { ...tile.meta, drill } };
}

// A dataset (time_series / split_totals) drawn as a plain table: one row per
// category/period -> its count. Fetches via the shared useTileItems pipeline (so
// a composite data_set.key resolves live) and reuses Worklist for the actual
// table chrome, exactly as ChartTile's Table toggle does. This is what makes the
// "Table" display option render for a built card, not only for a worklist source.
function DatasetTable({
  tile,
  onDrill,
  createdAfter,
}: TileBodyProps & { createdAfter?: string | null }) {
  const [cardFilters, setCardFilters] = useCardFilters(tile);
  const { items, isPending, env } = useTileItems(tile, createdAfter, cardFilters);
  const { records, tableIsRecords } = useRecordsBinding(tile);
  if (tableIsRecords && records) {
    return (
      <LiveTable
        key={`records:${tile.key}`}
        source={records.source!}
        binding={records}
        tableId={`dashboard-tile-${tile.key}-records`}
      />
    );
  }
  const filters = (
    <HeaderControls>
      <CardFilters facets={env?.filters} value={cardFilters} onChange={setCardFilters} />
    </HeaderControls>
  );
  if (isPending) {
    return (
      <div className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        {filters}
        Loading...
      </div>
    );
  }
  const drillTile = withDrill(tile, records, cardFilters);
  const columns = countColumns(tile, env);
  const rows: Datum[] = items.map((d) => ({
    meta: { x: d.label, v: d.value, k: d.meta?.key },
  }));
  return (
    <>
      {filters}
      <Worklist
        tile={{ ...drillTile, data: { items: rows }, meta: { ...drillTile.meta, columns } }}
        onDrill={onDrill}
        showColumnControl={false}
      />
    </>
  );
}

// action_list drawn as reminders: each Datum is label + secondary (when) +
// state==='due' (red dot).
function Followups({
  tile,
  createdAfter,
}: {
  tile: TileDef;
  createdAfter?: string | null;
}) {
  const { t } = useTranslation();
  const { items, isPending } = useTileItems(tile, createdAfter);
  // loading -> empty gate (never flash an empty state while the fetch is live).
  if (isPending) {
    return (
      <div className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        Loading...
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        {t("dashboard.noResults", "No results meet the parameter.")}
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      {items.map((f, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] py-[11px] last:border-b-0"
        >
          <span
            className={`h-[7px] w-[7px] flex-none rounded-full ${f.state === "due" ? "bg-[var(--status-error)]" : "bg-[var(--green)]"}`}
          />
          <div className="flex-1 text-[13px] font-medium text-[var(--ink)]">
            {f.label}
          </div>
          <span className="font-mono text-[11px] text-[var(--ink-3)]">
            {f.secondary}
          </span>
        </div>
      ))}
    </div>
  );
}

// split_totals drawn as a ranked list: label + secondary (sub) + value, meta.pct
// drives the bar width.
function Leaderboard({
  tile,
  createdAfter,
  onDrill,
}: {
  tile: TileDef;
  createdAfter?: string | null;
  onDrill?: DrillHandler;
}) {
  const { t } = useTranslation();
  const [cardFilters, setCardFilters] = useCardFilters(tile);
  const { items, isPending, env } = useTileItems(tile, createdAfter, cardFilters);
  const { records } = useRecordsBinding(tile);
  const drillTile = withDrill(tile, records, cardFilters);
  const columns = countColumns(tile, env);
  if (isPending) {
    return (
      <div className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }
  // Every ranked row is listed; a long board scrolls within the tile.
  return (
    <div className="-mr-2 flex max-h-[440px] flex-col overflow-y-auto pr-2">
      <HeaderControls>
        <CardFilters facets={env?.filters} value={cardFilters} onChange={setCardFilters} />
      </HeaderControls>
      {items.map((r, i) => {
        const pct = Number(cellsOf(r).pct ?? 0);
        // A live breakdown row opens the records it counted.
        const drill = onDrill
          ? segmentRecords(drillTile, cellsOf(r).key ?? r.label)
          : undefined;
        const body = (
          <>
            <span
              className={`font-display text-[20px] tabular-nums ${i === 0 ? "text-[var(--green)]" : "text-[var(--ink-3)]"}`}
            >
              {i + 1}
            </span>
            <div className="min-w-0">
              <b className="block truncate font-medium text-[var(--ink)]">
                {r.label}
              </b>
              <small className="block truncate text-[11px] text-[var(--ink-3)]">
                {r.secondary}
              </small>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="h-1.5 w-[90px] overflow-hidden rounded bg-[var(--bg-subtle)]">
                <span
                  className="block h-full bg-[var(--green)]"
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span className="min-w-[40px] text-right text-[12px] tabular-nums text-[var(--ink-2)]">
                {r.value}
              </span>
            </div>
          </>
        );
        const rowCls =
          "grid grid-cols-[20px_1fr_auto] items-center gap-3 border-b-[0.5px] border-[var(--rule)] py-2.5 last:border-b-0";
        return drill && onDrill ? (
          <Button
            key={`${r.label}-${i}`}
            variant="ghost"
            onClick={() =>
              onDrill(
                tile.title ?? "Card",
                String(r.label ?? ""),
                [
                  { l: columns[0].label, v: String(r.label ?? "") },
                  { l: columns[1].label, v: String(r.value ?? "") },
                ],
                drill,
              )
            }
            className={`${rowCls} h-auto w-full whitespace-normal rounded-[6px] px-0 text-left font-normal hover:bg-[var(--bg-subtle)]`}
          >
            {body}
          </Button>
        ) : (
          <div key={`${r.label}-${i}`} className={rowCls}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

// Pass/fail city pins drawn imperatively on the Google map (classic Marker so no
// Cloud map id is required). Green = passed / active, red = failed.
function CityPins({ cities }: { cities: GeoPoint[] }) {
  const map = useMap();
  const key = cities
    .map((c) => `${c.lat},${c.lng},${c.fail ? "f" : "p"}`)
    .join("|");
  useEffect(() => {
    if (!map || cities.length === 0) return;
    const markers = cities.map(
      (c) =>
        new google.maps.Marker({
          map,
          position: { lat: c.lat, lng: c.lng },
          title: c.name,
          icon: {
            url:
              "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">` +
                  `<circle cx="11" cy="11" r="8" fill="${c.fail ? "#a5372b" : "#1c5a48"}" stroke="#ffffff" stroke-width="2.5"/>` +
                  `</svg>`,
              ),
            scaledSize: new google.maps.Size(22, 22),
            anchor: new google.maps.Point(11, 11),
          },
        }),
    );
    return () => markers.forEach((m) => m.setMap(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

function MapLegend({ perf }: { perf: boolean }) {
  return (
    <div className="absolute bottom-3 left-3 z-10 flex gap-3.5 rounded-lg bg-[color-mix(in_srgb,var(--bg-card)_82%,transparent)] px-2.5 py-1.5 text-[11px] text-[var(--ink-2)] backdrop-blur-sm">
      {perf ? (
        <span>
          <i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[var(--green)] align-middle" />
          Registrations by area
        </span>
      ) : (
        <>
          <span>
            <i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[var(--green)] align-middle" />
            Passed
          </span>
          <span>
            <i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[var(--status-error)] align-middle" />
            Failed
          </span>
        </>
      )}
    </div>
  );
}

// Project a lat/lng onto the stylised map box using a rough North America
// bounding box (lat 24..50, lng -125..-66). Used only for the no-Maps-key
// fallback so the dots still reflect real coordinates, never hardcoded pins.
function projectCity(lat: number, lng: number): { x: number; y: number } {
  const x = ((lng + 125) / (125 - 66)) * 100;
  const y = ((50 - lat) / (50 - 24)) * 100;
  return {
    x: Math.min(96, Math.max(4, x)),
    y: Math.min(92, Math.max(8, y)),
  };
}

// The map canvas: a real Google map of North America when there are points AND a
// Maps API key is configured (launchpad / sandbox); otherwise a stylised dot map
// plotting the same real coordinates, so the tile always renders (e.g. local dev,
// where VITE_GOOGLE_MAPS_API_KEY is unset).
function MapCanvas({ cities, perf }: { cities: GeoPoint[]; perf: boolean }) {
  if (cities.length > 0 && !missingMapConfig()) {
    return (
      <div className="relative h-[250px] overflow-hidden rounded-[12px] border-[0.5px] border-[var(--rule)]">
        <APIProvider apiKey={MAPS_KEY}>
          <GoogleMap
            defaultCenter={{ lat: 39.5, lng: -98.35 }}
            defaultZoom={4}
            gestureHandling="greedy"
            disableDefaultUI
            styles={CLARA_VALE_MAP_STYLES}
            className="h-full w-full"
          >
            <CityPins cities={cities} />
          </GoogleMap>
        </APIProvider>
        <MapLegend perf={perf} />
      </div>
    );
  }
  return (
    <div className="relative h-[250px] overflow-hidden rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] bg-[radial-gradient(1px_1px_at_20px_20px,var(--rule)_1px,transparent_0)] bg-[length:26px_26px]">
      {cities.length === 0 ? (
        <div className="grid h-full place-items-center">
          <p className="text-[13px] text-[var(--ink-3)]">
            No locations to plot.
          </p>
        </div>
      ) : (
        cities.map((c, i) => {
          const p = projectCity(c.lat, c.lng);
          return (
            <span
              key={`${c.name}-${i}`}
              title={c.name}
              className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full ${c.fail ? "bg-[color-mix(in_srgb,var(--status-error)_82%,transparent)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--status-error)_16%,transparent)]" : "bg-[color-mix(in_srgb,var(--green)_82%,transparent)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--green)_16%,transparent)]"}`}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: "16px",
                height: "16px",
              }}
            />
          );
        })
      )}
      {cities.length > 0 && <MapLegend perf={perf} />}
    </div>
  );
}

// Grid view of the same geo points - a scannable list of locations with their
// pass/fail state. The same envelope drawn a different way.
function MapGrid({ cities, perf }: { cities: GeoPoint[]; perf: boolean }) {
  if (cities.length === 0) {
    return (
      <p className="py-6 text-center text-[14px] text-[var(--ink-2)]">
        No locations to plot.
      </p>
    );
  }
  return (
    <div className="grid max-h-[250px] grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2 overflow-auto pr-1">
      {cities.map((c, i) => (
        <div
          key={`${c.name}-${i}`}
          className="flex items-center gap-2.5 rounded-[10px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2.5"
        >
          <span
            className={`h-2.5 w-2.5 flex-none rounded-full ${c.fail ? "bg-[var(--status-error)]" : "bg-[var(--green)]"}`}
          />
          <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--ink)]">
            {c.name}
          </span>
          {!perf && (
            <span
              className={`text-[11px] font-medium ${c.fail ? "text-[var(--status-error)]" : "text-[var(--green)]"}`}
            >
              {c.fail ? "Failed" : "Passed"}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// A geo_points tile: the same Datum list drawn as a Map or a Grid, toggled
// in-body (like the chart tiles' Chart/Table toggle) - one renderer, two draws.
function MapTile({
  tile,
  createdAfter,
}: {
  tile: TileDef;
  createdAfter?: string | null;
}) {
  const cities = useTileItems(tile, createdAfter).items.map(toGeo);
  const perf = tile.meta?.mode === "perf";
  const [mode, setMode] = useState<"map" | "grid">("map");
  const segBtn = (active: boolean) =>
    `h-auto rounded-full px-3 py-[3px] text-[11px] font-normal transition-all ${
      active
        ? "bg-[var(--bg-card)] text-[var(--ink)] shadow-[0_1px_2px_rgba(26,26,24,0.08)]"
        : "text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
    }`;
  return (
    <div>
      <HeaderControls>
        <div className="inline-flex rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-0.5">
          <Button
            variant="ghost"
            aria-pressed={mode === "map"}
            onClick={() => setMode("map")}
            className={segBtn(mode === "map")}
          >
            Map
          </Button>
          <Button
            variant="ghost"
            aria-pressed={mode === "grid"}
            onClick={() => setMode("grid")}
            className={segBtn(mode === "grid")}
          >
            Grid
          </Button>
        </div>
      </HeaderControls>
      {mode === "grid" ? (
        <MapGrid cities={cities} perf={perf} />
      ) : (
        <MapCanvas cities={cities} perf={perf} />
      )}
    </div>
  );
}

// action_list drawn as a grid of shortcuts: label + secondary (description) +
// meta.icon.
function QuickActions({ tile }: { tile: TileDef }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5">
      {itemsOf(tile).map((a) => (
        <Button
          key={a.label}
          variant="ghost"
          className="flex h-auto w-full items-center justify-start gap-3 whitespace-normal rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-[14px] text-left font-normal transition-colors hover:border-[var(--green-border)] hover:bg-[var(--green-bg)]"
        >
          <span className="grid h-8 w-8 flex-none place-items-center rounded-[9px] bg-[var(--green-bg)] text-[16px] text-[var(--green)]">
            {String(cellsOf(a).icon ?? "")}
          </span>
          <span>
            <b className="text-[13.5px] font-medium text-[var(--ink)]">
              {a.label}
            </b>
            <small className="block text-[11px] text-[var(--ink-3)]">
              {a.secondary}
            </small>
          </span>
        </Button>
      ))}
    </div>
  );
}

// ── Alerts feed ────────────────────────────────────────────────────────────
// The alerts tile is the user's live notifications rendered as an activity feed
// (the pre-redesign design), sourced from the ONE notification infrastructure -
// never a bespoke alert model. Dot colour = notification level; the pill names
// the surface; the record id + title + owner + time complete the row.

function alertDotClass(level: NotificationRow["level"]): string {
  if (level === "critical") return "bg-[var(--status-error)]";
  if (level === "attention") return "bg-[var(--status-warn)]";
  return "border border-[var(--ink-3)] bg-transparent";
}

function titleCaseRecordType(recordType: string): string | undefined {
  const cleaned = recordType.trim().replace(/[_-]+/g, " ");
  if (!cleaned) return undefined;
  return cleaned
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function alertBadgeLabel(
  alert: NotificationRow,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const kind =
    (typeof alert.context?.reason === "string" && alert.context.reason) ||
    (typeof alert.context?.kind === "string" && alert.context.kind) ||
    "";
  const KIND_LABELS: Record<string, string> = {
    awaiting_first_decision: t("dashboard.alerts.newClaim"),
    message: t("dashboard.alerts.customerReply"),
    info_response: t("dashboard.alerts.infoProvided"),
    external_upload: t("dashboard.alerts.upload"),
    evidence: t("dashboard.alerts.evidence"),
    assignment: t("dashboard.alerts.assigned"),
    callback_requested: t("dashboard.alerts.callback"),
  };
  const CATEGORY_LABELS: Record<NotificationRow["category"], string> = {
    customer_message: t("dashboard.alerts.message"),
    action_required: t("dashboard.alerts.actionNeeded"),
    assignment: t("dashboard.alerts.assigned"),
    mention: t("dashboard.alerts.mention"),
    system: t("dashboard.alerts.system"),
  };
  return (
    KIND_LABELS[kind] ??
    titleCaseRecordType(alert.record_type) ??
    CATEGORY_LABELS[alert.category] ??
    t("dashboard.alerts.alert")
  );
}

function alertRecordId(alert: NotificationRow): string {
  const uuid = alert.record_uuid;
  if (!uuid) return "";
  if (alert.record_type === "claim") return formatClaimId(uuid);
  if (alert.record_type === "holding" || alert.record_type === "registration") {
    return formatHoldingId(uuid);
  }
  const prefix = alert.record_type.slice(0, 3).toUpperCase() || "REC";
  return formatRecordId(uuid, prefix);
}

function alertActorLabel(alert: NotificationRow): string | null {
  const name = (alert.actor_name || "").trim();
  if (!name || name.includes("@")) return null;
  if ((alert.body || "").toLowerCase().includes(name.toLowerCase()))
    return null;
  return name;
}

function alertTimeAgo(iso: string, locale: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(locale || undefined);
}

// Where the reader was in a feed (search, date range, scroll offset), kept per
// tab so opening a record and coming back lands them where they left off.
interface FeedViewState {
  search: string;
  scrollTop: number;
  range: TimeRange;
  custom?: { from: string; to: string };
}

const FEED_RANGES: readonly TimeRange[] = [
  "all_time",
  "this_week",
  "this_month",
  "last_month",
  "custom",
];

const FEED_VIEW_PREFIX = "raven:dashboard-feed-view:v1";
const DEFAULT_FEED_ORDERING = "-occurred_at";

function readFeedView(key: string | null): FeedViewState {
  const empty: FeedViewState = { search: "", scrollTop: 0, range: "all_time" };
  if (!key || typeof window === "undefined") return empty;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(key) ?? "null");
    const custom = parsed?.custom;
    const hasCustom =
      typeof custom?.from === "string" && typeof custom?.to === "string";
    const range: TimeRange = FEED_RANGES.includes(parsed?.range)
      ? parsed.range
      : "all_time";
    return {
      search: typeof parsed?.search === "string" ? parsed.search : "",
      scrollTop: typeof parsed?.scrollTop === "number" ? parsed.scrollTop : 0,
      // A custom range with no dates is no range at all.
      range: range === "custom" && !hasCustom ? "all_time" : range,
      custom: hasCustom ? { from: custom.from, to: custom.to } : undefined,
    };
  } catch {
    return empty;
  }
}

function writeFeedView(key: string | null, view: FeedViewState) {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(view));
  } catch {
    // Storage blocked: the feed still works, it just forgets its place.
  }
}

// The notifications activity feed, shared by two custom views: "Alerts" (unread
// only - what needs attention) and "Recent activity" (all statuses - the recent
// event stream). One row renderer, two data sources. Newest activity first; the
// type / assignee / date facets and sort options come from the server.
function NotificationFeed({
  tileKey,
  status,
  emptyText,
  rememberPosition,
}: {
  tileKey: string;
  status?: "unread" | "read" | "archived";
  emptyText: string;
  /** Restore search + scroll on return (the in-grid tile, not the expanded view). */
  rememberPosition: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale } = useFormattingContext();
  const { data: org } = useOrg();
  const tenantSlug = org?.organisation?.tenant_slug ?? null;
  const tableId = `dashboard-feed-${tileKey}`;
  const viewKey = tenantSlug
    ? `${FEED_VIEW_PREFIX}:${tenantSlug}:${tileKey}`
    : null;

  const [filterValues, setFilterValues] = useTableFilterPreferences(tableId);
  const ordering = useUserPreference<string>(
    `${tableId}-ordering`,
    DEFAULT_FEED_ORDERING,
  );
  const [initialView] = useState(() => readFeedView(viewKey));
  const [search, setSearch] = useState(initialView.search);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [range, setRange] = useState<TimeRange>(initialView.range);
  const [custom, setCustom] = useState(initialView.custom);

  const params = useMemo(() => {
    const p: NotificationFeedParams = {};
    if (status) p.status = status;
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(",");
    }
    if (debouncedSearch) p.search = debouncedSearch;
    if (ordering.value !== DEFAULT_FEED_ORDERING) p.ordering = ordering.value;
    return { ...p, ...timeRangeToParams(range, custom) };
  }, [status, filterValues, debouncedSearch, ordering.value, range, custom]);

  const feed = useNotificationFeed(params);
  const pages = feed.data?.pages ?? [];
  const alerts = pages.flatMap((page) => page.results);
  const total = pages[0]?.count ?? 0;
  const facets = pages[0]?.filter_options ?? [];
  const sortOptions = pages[0]?.sort_options ?? [];
  const filtering =
    Boolean(debouncedSearch) ||
    range !== "all_time" ||
    Object.values(filterValues).some((values) => values.length > 0);

  const listRef = useRef<HTMLDivElement>(null);
  const restored = useRef(!rememberPosition);
  const saveTimer = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (restored.current || !listRef.current || alerts.length === 0) return;
    restored.current = true;
    listRef.current.scrollTop = initialView.scrollTop;
  }, [alerts.length, initialView.scrollTop]);
  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  // The debounced scroll save runs after later renders; read the view from a
  // ref so it never writes back the search / range its scroll started with.
  const viewRef = useRef({ search, range, custom });
  useEffect(() => {
    viewRef.current = { search, range, custom };
  }, [search, range, custom]);

  const saveView = (next: Partial<FeedViewState> = {}) => {
    if (!rememberPosition) return;
    writeFeedView(viewKey, {
      ...viewRef.current,
      scrollTop: listRef.current?.scrollTop ?? 0,
      ...next,
    });
  };
  const backToTop = () => {
    if (listRef.current) listRef.current.scrollTop = 0;
  };
  const onScroll = () => {
    if (!rememberPosition) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => saveView(), 150);
  };
  const open = (target: string) => {
    saveView();
    navigate(target);
  };

  const toolbar = (
    <div className="mb-2 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            backToTop();
            saveView({ search: value, scrollTop: 0 });
          }}
          placeholder={t("dashboard.alerts.searchPlaceholder", {
            defaultValue: "Search customer or claim",
          })}
          aria-label={t("dashboard.alerts.searchPlaceholder", {
            defaultValue: "Search customer or claim",
          })}
          className="h-8 text-[12px]"
        />
      </div>
      <TimeFilter
        value={range}
        customRange={custom}
        onChange={(nextRange, nextCustom) => {
          setRange(nextRange);
          setCustom(nextCustom);
          backToTop();
          saveView({ range: nextRange, custom: nextCustom, scrollTop: 0 });
        }}
        triggerClassName="h-8 shrink-0 rounded-[8px] border-[0.5px] border-solid border-[var(--rule)] bg-[var(--bg)] px-3 text-[12px] text-[var(--ink)] no-underline hover:no-underline"
      />
      <ProductFilterMenu
        facets={facets}
        value={filterValues}
        onChange={(values) => {
          setFilterValues(values);
          backToTop();
        }}
        triggerClassName="h-8 text-[12px]"
      />
      {sortOptions.length > 0 && (
        <Select
          value={ordering.value}
          onValueChange={(value) => {
            if (!value) return;
            ordering.setValue(String(value));
            backToTop();
          }}
        >
          <SelectTrigger
            aria-label={t("dashboard.alerts.sortLabel", {
              defaultValue: "Sort by",
            })}
            className="h-8 w-[150px] shrink-0 text-[12px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  let body: ReactNode;
  if (feed.isPending || (alerts.length === 0 && feed.isFetching)) {
    body = (
      <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        {t("common.loading", { defaultValue: "Loading..." })}
      </p>
    );
  } else if (alerts.length === 0) {
    body = (
      <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        {filtering
          ? t("dashboard.alerts.noMatches", {
              defaultValue: "Nothing matches these filters.",
            })
          : emptyText}
      </p>
    );
  } else {
    body = (
      <div
        ref={listRef}
        onScroll={onScroll}
        className="-mr-2 flex max-h-[420px] flex-col overflow-y-auto pr-2"
      >
        {alerts.map((alert, i) => {
          const target = alert.link_url || null;
          const badgeLabel = alertBadgeLabel(alert, t);
          const recordId = alert.record_reference || alertRecordId(alert);
          const actorLabel = alertActorLabel(alert);
          const repeats =
            alert.status === "unread" ? (alert.event_count ?? 1) : 1;
          return (
            <div
              key={alert.uuid}
              role={target ? "button" : undefined}
              tabIndex={target ? 0 : undefined}
              onClick={target ? () => open(target) : undefined}
              onKeyDown={
                target
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        open(target);
                      }
                    }
                  : undefined
              }
              className={cn(
                "flex items-start gap-2.5 py-3",
                target &&
                  "cursor-pointer transition-opacity duration-100 hover:opacity-70",
                i < alerts.length - 1 &&
                  "border-b-[0.5px] border-[var(--rule)]",
              )}
            >
              <span
                className={cn(
                  "mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                  alertDotClass(alert.level),
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <Badge className="h-auto shrink-0 rounded-[3px] border-transparent bg-[var(--green-bg-muted)] px-[7px] py-[2px] font-mono text-[10px] font-medium uppercase tracking-[0.02em] text-[var(--green)]">
                    {badgeLabel}
                  </Badge>
                  <p className="truncate text-[12px] text-[var(--ink)]">
                    {recordId && (
                      <span className="font-mono text-[11.5px] tabular-nums text-[var(--ink-3)]">
                        {recordId}
                        {" · "}
                      </span>
                    )}
                    {alert.title}
                  </p>
                  {repeats > 1 && (
                    <Badge className="h-auto shrink-0 rounded-[3px] border-transparent bg-[var(--status-warn)]/15 px-[6px] py-[1px] font-mono text-[10px] font-medium tabular-nums text-[var(--ink)]">
                      {t("dashboard.alerts.newCount", {
                        count: repeats,
                        defaultValue: "{{count}} new",
                      })}
                    </Badge>
                  )}
                </div>
                {alert.body && (
                  <p className="mt-1 line-clamp-2 text-[11px] text-[var(--ink-3)]">
                    {alert.body}
                  </p>
                )}
                {actorLabel && (
                  <p className="mt-1 truncate text-[11px] text-[var(--ink-3)]">
                    {actorLabel}
                  </p>
                )}
                {alert.record_type === "claim" && (
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px]">
                    <span className="flex min-w-0 items-baseline gap-1">
                      <span className="text-[var(--ink-3)]">
                        {t("dashboard.alerts.assigneeLabel", {
                          defaultValue: "Assignee",
                        })}
                        :
                      </span>
                      <span
                        className={cn(
                          "truncate",
                          alert.assignee_name
                            ? alert.assignee_is_viewer
                              ? "font-medium text-[var(--ink)]"
                              : "text-[var(--ink-2)]"
                            : "italic text-[var(--ink-3)]",
                        )}
                      >
                        {alert.assignee_name
                          ? alert.assignee_is_viewer
                            ? t("dashboard.alertOwnerYou", {
                                defaultValue: "You",
                              })
                            : alert.assignee_name
                          : t("dashboard.alertUnassigned", {
                              defaultValue: "Unassigned",
                            })}
                      </span>
                    </span>
                    {alert.customer_name && (
                      <span className="flex min-w-0 items-baseline gap-1">
                        <span className="text-[var(--ink-3)]">
                          {t("dashboard.alerts.customerLabel", {
                            defaultValue: "Customer",
                          })}
                          :
                        </span>
                        <span className="truncate text-[var(--ink-2)]">
                          {alert.customer_name}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="shrink-0 whitespace-nowrap font-mono text-[11px] tabular-nums text-[var(--ink-3)]">
                {alertTimeAgo(alert.occurred_at || alert.created_at, locale)}
              </span>
            </div>
          );
        })}
        <div className="flex items-center justify-between gap-3 border-t-[0.5px] border-[var(--rule)] pt-3">
          <span className="font-mono text-[11px] tabular-nums text-[var(--ink-3)]">
            {t("dashboard.alerts.showing", {
              shown: alerts.length,
              total,
              defaultValue: "{{shown}} of {{total}}",
            })}
          </span>
          {feed.hasNextPage && (
            <Button
              variant="outline"
              size="xs"
              disabled={feed.isFetchingNextPage}
              onClick={() => feed.fetchNextPage()}
            >
              {feed.isFetchingNextPage
                ? t("dashboard.alerts.loadingMore", {
                    defaultValue: "Loading...",
                  })
                : t("dashboard.alerts.loadMore", { defaultValue: "Load more" })}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {toolbar}
      {body}
    </div>
  );
}

// "Alerts" view - unread notifications (what needs attention).
function AlertsFeed({
  tileKey,
  rememberPosition,
}: {
  tileKey: string;
  rememberPosition: boolean;
}) {
  const { t } = useTranslation();
  return (
    <NotificationFeed
      tileKey={tileKey}
      status="unread"
      rememberPosition={rememberPosition}
      emptyText={t("dashboard.noAlerts", {
        defaultValue: "No results meet the parameter.",
      })}
    />
  );
}

// "Recent activity" view - the recent event stream regardless of read state.
function ActivityFeed({
  tileKey,
  rememberPosition,
}: {
  tileKey: string;
  rememberPosition: boolean;
}) {
  const { t } = useTranslation();
  return (
    <NotificationFeed
      tileKey={tileKey}
      rememberPosition={rememberPosition}
      emptyText={t("dashboard.noActivity", {
        defaultValue: "No recent activity.",
      })}
    />
  );
}

// The record a workflow issue sits on, for the row's link.
const ISSUE_RECORD_PATH: Record<string, string> = {
  holding: "/registrations",
  registration: "/registrations",
  warranty: "/registrations",
  claim: "/claims",
  transfer: "/transfers",
  inspection: "/inspections",
};

// "Work queue" view - open workflow issues needing action (approval / availability
// / exit-check failures the engine parked on a record). Each row links to the
// record so a user can go resolve it.
function WorkQueue() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale } = useFormattingContext();
  const { data, isPending } = useWorkQueue();
  const issues = data ?? [];

  if (isPending) {
    return (
      <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
        Loading...
      </p>
    );
  }
  if (issues.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="text-[26px] text-[var(--green)]">✦</div>
        <h4 className="mt-2 font-display text-[22px] font-light text-[var(--ink)]">
          {t("dashboard.noWorkQueue", {
            defaultValue: "Nothing needs action.",
          })}
        </h4>
      </div>
    );
  }
  return (
    <div className="-mr-2 flex max-h-[420px] flex-col overflow-y-auto pr-2">
      {issues.map((issue, i) => {
        const base = ISSUE_RECORD_PATH[issue.resource_type];
        const target = base ? `${base}/${issue.resource_id}` : null;
        const recordId = issue.context?.record_number;
        const badge = (
          issue.source_state_label ||
          issue.issue_kind ||
          "issue"
        ).replace(/_/g, " ");
        const assignee = issue.context?.assigned_to_name;
        return (
          <div
            key={issue.uuid}
            role={target ? "button" : undefined}
            tabIndex={target ? 0 : undefined}
            onClick={target ? () => navigate(target) : undefined}
            onKeyDown={
              target
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(target);
                    }
                  }
                : undefined
            }
            className={cn(
              "flex items-start gap-2.5 py-3",
              target &&
                "cursor-pointer transition-opacity duration-100 hover:opacity-70",
              i < issues.length - 1 && "border-b-[0.5px] border-[var(--rule)]",
            )}
          >
            <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--green)]" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <Badge className="h-auto shrink-0 rounded-[3px] border-transparent bg-[var(--green-bg-muted)] px-[7px] py-[2px] font-mono text-[10px] font-medium uppercase tracking-[0.02em] text-[var(--green)]">
                  {badge}
                </Badge>
                <p className="truncate text-[12px] text-[var(--ink)]">
                  {recordId && (
                    <span className="font-mono text-[11.5px] tabular-nums text-[var(--ink-3)]">
                      {recordId}
                      {" · "}
                    </span>
                  )}
                  {issue.summary}
                </p>
              </div>
              {issue.detail && issue.detail !== issue.summary && (
                <p className="mt-1 line-clamp-2 text-[11px] text-[var(--ink-3)]">
                  {issue.detail}
                </p>
              )}
              {assignee && (
                <p className="mt-1 flex items-center gap-1.5 truncate text-[11px] text-[var(--ink-2)]">
                  <User className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate">{assignee}</span>
                </p>
              )}
            </div>
            <span className="shrink-0 whitespace-nowrap font-mono text-[11px] tabular-nums text-[var(--ink-3)]">
              {alertTimeAgo(issue.created_at, locale)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function TileBody({
  tile,
  onDrill,
  createdAfter,
  manage = false,
}: TileBodyProps & { manage?: boolean }) {
  switch (tile.render_type) {
    case "stat":
      return <Kpi tile={tile} createdAfter={createdAfter} />;
    case "bar_chart":
      return (
        <ChartTile
          tile={tile}
          onDrill={onDrill}
          kind="bar"
          createdAfter={createdAfter}
        />
      );
    case "pie_chart":
      return (
        <ChartTile
          tile={tile}
          onDrill={onDrill}
          kind="pie"
          createdAfter={createdAfter}
        />
      );
    case "line_chart":
      return (
        <ChartTile
          tile={tile}
          onDrill={onDrill}
          kind="line"
          createdAfter={createdAfter}
        />
      );
    case "followups":
      return <Followups tile={tile} createdAfter={createdAfter} />;
    case "alerts":
      return <AlertsFeed tileKey={tile.key} rememberPosition={!manage} />;
    case "activity":
      return <ActivityFeed tileKey={tile.key} rememberPosition={!manage} />;
    case "work_queue":
      return <WorkQueue />;
    case "leaderboard":
      return <Leaderboard tile={tile} createdAfter={createdAfter} onDrill={onDrill} />;
    case "map":
      return <MapTile tile={tile} createdAfter={createdAfter} />;
    case "heat_map":
      return <HeatMapTile tile={tile} createdAfter={createdAfter} onDrill={onDrill} />;
    case "action_grid":
      return <QuickActions tile={tile} />;
    case "table":
      // A dataset-backed table (composite "<entity>:<measure>" key) renders its
      // live series/segments as a category -> count table; inline-item tables
      // (seeded claim worklists) keep the full Worklist chrome.
      if (tile.data_set?.key?.includes(":")) {
        return (
          <DatasetTable
            tile={tile}
            onDrill={onDrill}
            createdAfter={createdAfter}
          />
        );
      }
      return (
        <Worklist tile={tile} onDrill={onDrill} showColumnControl={manage} />
      );
    default:
      return (
        <Worklist tile={tile} onDrill={onDrill} showColumnControl={manage} />
      );
  }
}
